import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Building2, User, Users, ChevronRight, ChevronDown, AlertCircle, RefreshCw } from 'lucide-react';
import { omAPI } from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';

const OrgChart = () => {
  const navigate = useNavigate();
  
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedRootOrgUnit, setSelectedRootOrgUnit] = useState('');
  const [orgUnits, setOrgUnits] = useState([]);

  const loadOrgUnits = useCallback(async () => {
    try {
      const res = await omAPI.getOrgUnits({ status: 'Active' });
      setOrgUnits(res.data || []);
    } catch (error) {
      console.error('Failed to load org units:', error);
    }
  }, []);

  const loadChartData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await omAPI.getOrgChart(selectedRootOrgUnit || undefined);
      setChartData(res.data);
      
      // Auto-expand root nodes
      const newExpanded = {};
      (res.data?.root_nodes || []).forEach(node => {
        newExpanded[node.id] = true;
      });
      setExpandedNodes(prev => ({ ...prev, ...newExpanded }));
    } catch (error) {
      console.error('Failed to load org chart:', error);
      toast.error('Failed to load organization chart');
    } finally {
      setLoading(false);
    }
  }, [selectedRootOrgUnit]);

  useEffect(() => {
    loadOrgUnits();
  }, [loadOrgUnits]);

  useEffect(() => {
    loadChartData();
  }, [loadChartData]);

  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const expandAll = () => {
    const allIds = {};
    const collectIds = (nodes) => {
      nodes.forEach(node => {
        allIds[node.id] = true;
        if (node.children) collectIds(node.children);
      });
    };
    if (chartData?.root_nodes) collectIds(chartData.root_nodes);
    setExpandedNodes(allIds);
  };

  const collapseAll = () => {
    // Keep only root nodes expanded
    const rootIds = {};
    (chartData?.root_nodes || []).forEach(node => {
      rootIds[node.id] = true;
    });
    setExpandedNodes(rootIds);
  };

  const getPositionStatusColor = (status) => {
    switch (status) {
      case 'VACANT': return 'border-yellow-400 bg-yellow-50';
      case 'OCCUPIED': return 'border-green-400 bg-green-50';
      case 'PLANNED': return 'border-orange-400 bg-orange-50';
      case 'OBSOLETE': return 'border-gray-400 bg-gray-50';
      default: return 'border-gray-300 bg-white';
    }
  };

  const renderNode = useCallback((node, level = 0) => {
    const isExpanded = expandedNodes[node.id];
    const hasChildren = node.children && node.children.length > 0;
    const isOrgUnit = node.type === 'org_unit';
    
    return (
      <div key={node.id} className="select-none">
        <div 
          className={`flex items-start gap-2 py-2 px-3 hover:bg-gray-50 transition-colors rounded-lg mb-1`}
          style={{ marginLeft: `${level * 24}px` }}
        >
          {/* Expand/Collapse button */}
          {hasChildren ? (
            <button 
              onClick={() => toggleNode(node.id)} 
              className="p-1 hover:bg-gray-200 rounded mt-0.5 flex-shrink-0"
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <div className="w-6 flex-shrink-0" />
          )}
          
          {/* Node content */}
          {isOrgUnit ? (
            // Org Unit Node
            <div className="flex-1 border-2 border-orange-300 rounded-lg p-3 bg-orange-50/50">
              <div className="flex items-center gap-2 mb-1">
                <Building2 className="h-5 w-5 text-orange-600 flex-shrink-0" />
                <span className="font-mono text-xs text-orange-600 font-semibold">{node.id}</span>
              </div>
              <h3 className="font-semibold text-gray-900">{node.name}</h3>
              {node.short_name && <p className="text-xs text-gray-500">{node.short_name}</p>}
              <div className="flex items-center gap-3 mt-2 text-xs text-gray-600">
                {node.location && <span className="flex items-center gap-1">{node.location}</span>}
                {node.position_count > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    {node.position_count} positions
                  </span>
                )}
              </div>
            </div>
          ) : (
            // Position Node
            <div className={`flex-1 border-2 rounded-lg p-3 ${getPositionStatusColor(node.position_status)}`}>
              <div className="flex items-center gap-2 mb-1">
                <User className={`h-4 w-4 flex-shrink-0 ${node.holder_name ? 'text-green-600' : 'text-yellow-600'}`} />
                <span className="font-mono text-xs text-gray-600 font-semibold">{node.id}</span>
                {node.is_chief && (
                  <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">Chief</span>
                )}
              </div>
              <h4 className="font-medium text-gray-900 text-sm">{node.name}</h4>
              {node.job_name && <p className="text-xs text-gray-500">{node.job_name}</p>}
              {node.holder_name ? (
                <div className="mt-2 flex items-center gap-1 text-xs">
                  <div className="w-5 h-5 rounded-full bg-green-200 flex items-center justify-center text-green-800 text-xs font-semibold">
                    {node.holder_name.charAt(0)}
                  </div>
                  <span className="font-medium text-gray-700">{node.holder_name}</span>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-1 text-xs text-yellow-700">
                  <AlertCircle className="h-3 w-3" />
                  Vacant
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="border-l-2 border-gray-200 ml-5">
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  }, [expandedNodes]);

  return (
    <Layout>
      <div className="p-6" data-testid="org-chart-page">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm/om')}>Org Management</span>
            <span>/</span>
            <span>Org Chart</span>
          </div>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Organization Chart</h1>
              <p className="text-gray-600 mt-1">Visual representation of organizational structure</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={expandAll}>Expand All</Button>
              <Button variant="outline" size="sm" onClick={collapseAll}>Collapse All</Button>
              <Button variant="outline" size="sm" onClick={loadChartData}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="mb-4">
          <Select value={selectedRootOrgUnit || "all"} onValueChange={(val) => setSelectedRootOrgUnit(val === "all" ? "" : val)}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Show Full Organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Full Organization</SelectItem>
              {orgUnits.filter(ou => !ou.parent_org_unit_id).map(ou => (
                <SelectItem key={ou.org_unit_id} value={ou.org_unit_id}>{ou.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats Cards */}
        {chartData && (
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Building2 className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Org Units</p>
                  <p className="text-xl font-semibold">{chartData.total_org_units}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <User className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Positions</p>
                  <p className="text-xl font-semibold">{chartData.total_positions}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Employees</p>
                  <p className="text-xl font-semibold">{chartData.total_employees}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Vacant Positions</p>
                  <p className="text-xl font-semibold text-yellow-600">{chartData.vacant_positions}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Legend */}
        <div className="mb-4 flex items-center gap-6 text-sm">
          <span className="text-gray-500">Legend:</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-orange-300 bg-orange-50 rounded" />
            <span>Org Unit</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-green-400 bg-green-50 rounded" />
            <span>Occupied Position</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-yellow-400 bg-yellow-50 rounded" />
            <span>Vacant Position</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-1.5 py-0.5 text-xs bg-orange-100 text-orange-800 rounded">Chief</span>
            <span>Head Position</span>
          </div>
        </div>

        {/* Chart */}
        <Card className="p-4 overflow-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
          {loading ? (
            <div className="py-12 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Loading organization chart...</p>
            </div>
          ) : chartData?.root_nodes && chartData.root_nodes.length > 0 ? (
            <div className="min-w-[600px]">
              {chartData.root_nodes.map(node => renderNode(node))}
            </div>
          ) : (
            <div className="py-12 text-center text-gray-500">
              <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="font-medium">No organizational structure found</p>
              <p className="text-sm mt-1">Start by creating Org Units and Positions</p>
              <div className="mt-4 flex justify-center gap-2">
                <Button onClick={() => navigate('/hcm/om/org-units')}>Create Org Unit</Button>
                <Button variant="outline" onClick={() => navigate('/hcm/om/positions')}>Create Position</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default OrgChart;
